/**
 * Identity.
 *
 * Ids are **client-generated** (decision 0005) because a local-first store has
 * no server to allocate them, and a create that has to wait for one is not
 * local-first — it is a network call wearing a builder's clothing.
 *
 * UUIDv7 rather than v4: the first 48 bits are a millisecond timestamp, so ids
 * sort by creation. That buys two things this store depends on — a cursor is an
 * id comparison instead of an index, and a capped shape sample taken in id
 * order is the *same* sample on every render, which is what stops a role near
 * the coverage floor flickering between reloads.
 */

type Brand<T, B extends string> = T & { readonly __brand: B };

export type ObjectId      = Brand<string, 'ObjectId'>;
export type PropertyKeyId = Brand<string, 'PropertyKeyId'>;
export type AnchorId      = Brand<string, 'AnchorId'>;
export type EdgeId        = Brand<string, 'EdgeId'>;
export type ManifestRef   = Brand<string, 'ManifestRef'>;
export type JournalId     = Brand<string, 'JournalId'>;
export type Revision      = Brand<number, 'Revision'>;

const HEX = Array.from({ length: 256 }, (_, i) => i.toString(16).padStart(2, '0'));

/** Monotonic within a millisecond. Two objects created in the same tick would
 *  otherwise sort by their random tail, which is stable per run but different
 *  between runs — and an unstable order is an unstable sample. */
let lastMs = 0;
let seq = 0;

function uuidv7(): string {
  const ms = Date.now();
  if (ms === lastMs) seq += 1;
  else { lastMs = ms; seq = 0; }

  const b = new Uint8Array(16);
  crypto.getRandomValues(b);

  b[0] = Math.floor(ms / 2 ** 40) & 0xff;
  b[1] = Math.floor(ms / 2 ** 32) & 0xff;
  b[2] = Math.floor(ms / 2 ** 24) & 0xff;
  b[3] = Math.floor(ms / 2 ** 16) & 0xff;
  b[4] = Math.floor(ms / 2 ** 8) & 0xff;
  b[5] = ms & 0xff;
  // Version 7 nibble, then the 12-bit counter that keeps same-millisecond ids ordered.
  b[6] = 0x70 | ((seq >> 8) & 0x0f);
  b[7] = seq & 0xff;
  b[8] = (b[8]! & 0x3f) | 0x80;              // RFC 4122 variant

  const h = (i: number) => HEX[b[i]!]!;
  return `${h(0)}${h(1)}${h(2)}${h(3)}-${h(4)}${h(5)}-${h(6)}${h(7)}-${h(8)}${h(9)}-`
       + `${h(10)}${h(11)}${h(12)}${h(13)}${h(14)}${h(15)}`;
}

export function newObjectId(): ObjectId { return uuidv7() as ObjectId; }
export function newPropertyKeyId(): PropertyKeyId { return uuidv7() as PropertyKeyId; }
export function newAnchorId(): AnchorId { return uuidv7() as AnchorId; }
export function newEdgeId(): EdgeId { return uuidv7() as EdgeId; }
export function newJournalId(): JournalId { return uuidv7() as JournalId; }

export const asRevision = (n: number): Revision => n as Revision;
